export const norm = (v) => (v || '').toString().toLowerCase();

export function parseAgesRange(agesStr) {
  const s = (agesStr || '').toString().toLowerCase().trim();
  if (!s) return { min: null, max: null };

  const cleaned = s
    .replace('ages:', '')
    .replace('age:', '')
    .replace('years old', '')
    .replace('years', '')
    .replace('year', '')
    .replace(/\s+/g, ' ')
    .trim();

  const dashMatch = cleaned.match(/(\d{1,2})\s*[-–]\s*(\d{1,2})/);
  if (dashMatch) return { min: Number(dashMatch[1]), max: Number(dashMatch[2]) };

  const toMatch = cleaned.match(/(\d{1,2})\s*(to)\s*(\d{1,2})/);
  if (toMatch) return { min: Number(toMatch[1]), max: Number(toMatch[3]) };

  const plusMatch = cleaned.match(/(\d{1,2})\s*\+/);
  if (plusMatch) return { min: Number(plusMatch[1]), max: null };

  const upMatch = cleaned.match(/(\d{1,2})\s*(and\s*up|up)/);
  if (upMatch) return { min: Number(upMatch[1]), max: null };

  const single = cleaned.match(/(\d{1,2})/);
  if (single) {
    const n = Number(single[1]);
    return { min: n, max: n };
  }

  return { min: null, max: null };
}

export function parseCourseDateToDateObj(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/**
 * Derive which weekday(s) a course meets on from its sections,
 * but ONLY count sections that are now or upcoming.
 */
export function getCourseMeetingDays(course, { graceMs = 5 * 60 * 1000 } = {}) {
  const WEEKDAYS = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  const now = new Date();
  const cutoff = new Date(now.getTime() - graceMs);

  const upcomingDates =
    course?.sections
      ?.map((s) =>
        parseCourseDateToDateObj(s?.startDate || s?.startDateTime || s?.start)
      )
      .filter(Boolean)
      .filter((d) => d >= cutoff)
      .sort((a, b) => a - b) || [];

  if (!upcomingDates.length) return [];

  const days = upcomingDates.map((d) => WEEKDAYS[d.getDay()]).filter(Boolean);
  return Array.from(new Set(days));
}

export function getCourseNextDate(course) {
  const now = new Date();

  const sectionDates =
    course?.sections
      ?.map((s) =>
        parseCourseDateToDateObj(s?.startDate || s?.startDateTime || s?.start)
      )
      .filter(Boolean)
      .filter((d) => d >= now)
      .sort((a, b) => a - b) || [];

  if (sectionDates.length) return sectionDates[0];

  const candidates = [
    course?.nextSessionDate,
    course?.nextSessionStart,
    course?.sidebar?.nextSessionDate,
    course?.nextSessionStartDate,
    course?.nextSessionStartTime,
    course?.startDate,
    course?.sidebar?.startDate,
  ];

  const futureCandidates = candidates
    .map((c) => parseCourseDateToDateObj(c))
    .filter(Boolean)
    .filter((d) => d >= now)
    .sort((a, b) => a - b);

  if (futureCandidates.length) return futureCandidates[0];
  return null;
}

/* ---------------------------------
   Matchers
----------------------------------*/
function courseMatchesCategory(course, activeFilter) {
  if (activeFilter === 'All') return true;
  const expected = norm(activeFilter.replaceAll(' ', '_').replaceAll('&', 'and'));
  return norm(course?.subject) === expected;
}

function courseMatchesAge(course, filters) {
  const { ageFilterEnabled, selectedAges } = filters || {};
  if (!ageFilterEnabled) return true;

  // Nothing selected = no active filter
  if (!selectedAges?.length) return true;

  const { min, max } = parseAgesRange(course?.ages);
  if (min == null && max == null) return false;

  return selectedAges.some((kidAgeNum) => {
    if (max == null) return kidAgeNum >= min;
    return kidAgeNum >= min && kidAgeNum <= max;
  });
}

function courseMatchesPrice(course, filters) {
  const { priceFilterEnabled, minPrice, maxPrice } = filters || {};
  if (!priceFilterEnabled) return true;

  const perClass =
    (course?.sidebar?.weeklyPrice || 0) /
    (course?.sidebar?.weeklyFrequency || 1);

  const minP = minPrice === '' ? null : Number(minPrice);
  const maxP = maxPrice === '' ? null : Number(maxPrice);

  if (minP != null && !Number.isNaN(minP) && perClass < minP) return false;
  if (maxP != null && !Number.isNaN(maxP) && perClass > maxP) return false;

  return true;
}

function courseMatchesDay(course, filters) {
  const { dayFilterEnabled, selectedDays } = filters || {};
  if (!dayFilterEnabled) return true;

  // Nothing selected = no active filter
  if (!selectedDays?.length) return true;

  const courseDays = getCourseMeetingDays(course);
  if (!courseDays.length) return false;

  return selectedDays.some((d) => courseDays.some((cd) => norm(cd) === norm(d)));
}

function courseMatchesDateRange(course, filters) {
  const { dateRange } = filters || {};
  if (!dateRange || dateRange === 'All upcoming dates') return true;

  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);

  let rangeStart = null;
  let rangeEnd = null;

  if (dateRange === 'Today') {
    rangeStart = todayStart;
    rangeEnd = todayEnd;
  } else if (dateRange === 'Next 7 days') {
    rangeStart = todayStart;
    rangeEnd = endOfDay(new Date(todayStart.getTime() + 7 * 24 * 60 * 60 * 1000));
  } else if (dateRange === 'Next 14 days') {
    rangeStart = todayStart;
    rangeEnd = endOfDay(new Date(todayStart.getTime() + 14 * 24 * 60 * 60 * 1000));
  }

  const courseDate = getCourseNextDate(course);
  if (!courseDate || !rangeStart || !rangeEnd) return false;

  return !(courseDate < rangeStart || courseDate > rangeEnd);
}

/* ---------------------------------
   Main export
----------------------------------*/
export function filterCourses(courses, activeFilter, filters) {
  const list = Array.isArray(courses) ? courses : [];

  return list
    .filter((c) => courseMatchesCategory(c, activeFilter))
    .filter((c) => courseMatchesAge(c, filters))
    .filter((c) => courseMatchesPrice(c, filters))
    .filter((c) => courseMatchesDay(c, filters))
    .filter((c) => courseMatchesDateRange(c, filters));
}